import { toast } from "@carbon6/c6-frontend-design";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { getReservedKeyContracts, type ReservedKeyContract } from "@/api";
import type { WindowEntry } from "@/components/modal";
import { FullscreenWindow } from "@/components/modal";
import { uploadRequiredKeys, UPLOAD_RESERVED_KEYS } from "@/components/label";
import { createLogger } from "@/logging";
import { windowStack } from "@/app/windowStack";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { resolveUploadErrorMessage, uploadEngine, type UploadJobView } from "@/upload";
import { UploadSetupForm } from "./UploadSetupForm";
import { UploadStatusBar } from "./UploadStatusBar";
import { UploadWindowController } from "./UploadWindowController";
import {
  activeToItems,
  countItems,
  scanToItems,
  UploadItemFilterRow,
  UploadItemList,
  type ListFilter,
} from "./uploadItems";
import { uploadWindows, type UploadWindowParams } from "./uploadWindow";
import { isDismissable } from "./uploadJobStatus";
import styles from "./uploadWindow.module.css";

const narrowUploadQuery = "(max-width: 820px)";
function subscribeUploadViewport(listener: () => void): () => void {
  const query = window.matchMedia(narrowUploadQuery);
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}
const isNarrowUploadViewport = () => window.matchMedia(narrowUploadQuery).matches;

const logger = createLogger("page/upload");

type UploadWindowContentProps = {
  entry: WindowEntry;
  // 스택에서 이 엔트리가 전체화면으로 보이는지. false 면 최소화 상태다(오버레이는 숨기고 mount 는 유지).
  active: boolean;
};

/**
 * 전체화면 창 스택의 datasetUpload 콘텐츠. 한 컴포넌트가 설정 단계와 진행 단계를 모두 그린다.
 * 컨트롤러는 창 수명 동안 유지된다. 그래서 업로드가 시작되면 좌측 설정 폼을 비활성(dim)으로 계속
 * 표시한다. 우측에는 파일 목록(설정: 스캔 미리보기, 진행: 전송)과 그 아래 하단 상태 바가 있다.
 * 진행·완료 상태와 전체 액션은 목록 옆이 아니라 목록 **아래**에 둔다. 호스트가 entry.id 로 key 를
 * 주므로 창마다 상태가 새로 생긴다. 최소화해도 mount 는 유지된다. 헤더와 닫기는 FullscreenWindow 가 소유한다.
 */
export function UploadWindowContent({ entry, active }: UploadWindowContentProps): ReactNode {
  const narrow = useSyncExternalStore(subscribeUploadViewport, isNarrowUploadViewport, () => false);
  const params = entry.params as UploadWindowParams;
  const folderInputId = `upload-folder-${entry.id}`;

  // 초기(설정) 파라미터를 1회 캡처한다. active 로 바뀌어도 컨트롤러와 폼 설정은 이 값으로 고정한다.
  const [setupInit] = useState(() => (params.phase === "setup" ? params : null));
  const setupSeed = setupInit?.seed ?? undefined;

  // 창(entry.id)마다 컨트롤러가 1개다. setup 에서 active 로 바뀌어도 폼 상태(제출한 값)를 보존한다.
  const [controller] = useState((): UploadWindowController => {
    const created: UploadWindowController = new UploadWindowController({
      folderInputId,
      enqueue: uploadEngine.enqueue,
      resolveErrorMessage: resolveUploadErrorMessage,
      onSubmitted: (engineJobId, displayName) =>
        uploadWindows.toActive(entry.id, engineJobId, displayName),
      // 창 안에서 취소한 뒤 배너를 최신 서버 상태로 갱신한다. 방금 취소한 잡을 지운다.
      onJobsRefresh: () => created.refreshResumeCandidate(),
    });
    return created;
  });

  const formSnapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const contracts = useDatasetReservedContracts(controller);
  // 필터 띠는 목록 페인 밖(그리드 첫 행)에 있으므로 선택 상태를 창이 소유한다.
  const [listFilter, setListFilter] = useState<ListFilter>("all");

  useEffect(() => {
    controller.init(setupSeed);
    return () => {
      controller.dispose();
    };
  }, [controller, setupSeed]);

  const engineSnapshot = useSyncExternalStore(uploadEngine.subscribe, uploadEngine.getSnapshot);
  const started = params.phase === "active";
  const job: UploadJobView | null =
    params.phase === "active"
      ? (engineSnapshot.jobs.find((candidate) => candidate.id === params.engineJobId) ?? null)
      : null;
  const dismissable = !started || job === null || isDismissable(job);

  // 설정·종료된 창은 닫고, 전송·서버 처리 중인 창은 유지한다.
  useEffect(() => {
    uploadWindows.setClosable(entry.id, dismissable);
  }, [entry.id, dismissable]);

  const handleClose = (): void => {
    if (!dismissable) {
      toast.info("업로드를 완료하거나 폐기하기 전에는 창을 닫을 수 없습니다.");
      return;
    }
    if (job !== null) {
      uploadEngine.dismiss(job.id);
    }
    uploadWindows.close(entry.id);
  };

  const items = started
    ? job !== null
      ? activeToItems(job, uploadEngine)
      : []
    : scanToItems(formSnapshot.dialog.form.scan);
  const counts = countItems(items);
  const expired = started && job === null;

  return (
    <FullscreenWindow
      open={active}
      title={entry.title}
      // 좁은 창은 폼부터 목록까지 셸이 스크롤하고, 2열일 때만 각 pane이 스크롤한다.
      bodyLayout={narrow ? "scroll" : "fill"}
      onMinimize={() => windowStack.minimize(entry.id)}
      onRequestClose={handleClose}
    >
      {expired ? (
        <EmptyState size="lg" action={{ label: "닫기", onClick: handleClose }}>
          이 업로드 Session은 새로고침으로 만료됐습니다. 서버에는 남아 있으니 데이터 업로드 현황에서
          이어서 진행해 주세요.
        </EmptyState>
      ) : (
        // 설정과 진행에 공통인 2단 레이아웃. 좌측은 설정 폼이다. 우측은 필터 띠, 파일 목록, 하단
        // 상태 바다. 필터 띠는 그리드 첫 행의 우측 칸만 채운다. 그래서 폼 첫 필드가 목록 상자와
        // 같은 높이에서 시작한다.
        <div className={styles.panel}>
          <div className={styles.leftScroll}>
            <UploadSetupForm
              controller={controller}
              dialog={formSnapshot.dialog}
              embodiments={formSnapshot.embodiments}
              embodimentCatalog={formSnapshot.embodimentCatalog}
              uploadLocations={formSnapshot.uploadLocations}
              folderInputId={folderInputId}
              disabled={started}
              onCancel={() => uploadWindows.close(entry.id)}
              dataset={{
                reservedContracts: contracts.contracts,
                reservedKeys: contracts.keys,
                reservedRequiredKeys: contracts.requiredKeys,
              }}
            />
          </div>
          <UploadItemFilterRow
            filter={listFilter}
            failedCount={counts.failed}
            onFilterChange={setListFilter}
          />
          {/* 목록 페인은 파일 목록과 하단 상태 바로 구성된다. 바는 목록 아래에 고정되고 목록만 스크롤한다. */}
          <section className={styles.listPane}>
            <UploadItemList
              items={items}
              filter={listFilter}
              emptyHint={started ? undefined : "폴더를 선택하면 업로드할 파일이 여기 표시됩니다."}
            />
            <UploadStatusBar
              job={job}
              scan={formSnapshot.dialog.form.scan}
              engine={uploadEngine}
              onClose={handleClose}
              counts={{ done: counts.done, failed: counts.failed, total: items.length }}
            />
          </section>
        </div>
      )}
    </FullscreenWindow>
  );
}

type ReservedContractsState = {
  contracts: readonly ReservedKeyContract[];
  keys: readonly string[];
  requiredKeys: readonly string[];
};

// 업로드의 예약 라벨 계약을 로드해 컨트롤러(강제 key)와 폼에 전달한다. 드롭다운 초기 선택은
// 계약이 아니라 uploadFormDefaults 가 소유한다. 계약은 창이 열린 뒤에 도착하기 때문이다.
// 로드가 실패하면 강제를 끄고 서버 검사에 맡긴다(현황 페이지와 같은 정책).
function useDatasetReservedContracts(controller: UploadWindowController): ReservedContractsState {
  const [reserved, setReserved] = useState<ReservedContractsState>({
    contracts: [],
    keys: [],
    requiredKeys: [],
  });

  useEffect(() => {
    let cancelled = false;
    void getReservedKeyContracts()
      .then((contracts) => {
        if (cancelled) {
          return;
        }
        const present = (key: string): boolean =>
          contracts.some((contract) => contract.key === key);
        const keys = UPLOAD_RESERVED_KEYS.filter(present);
        const required = new Set(uploadRequiredKeys(contracts));
        const requiredKeys = keys.filter((key) => required.has(key));
        controller.reservedRequiredKeys = requiredKeys;
        setReserved({ contracts, keys, requiredKeys });
      })
      .catch((error) => {
        logger.warn("reserved key contract load failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [controller]);

  return reserved;
}
